import { Component, computed, inject, signal } from "@angular/core";
import { DialogFactory } from "../../components/dialog/dialogFactory";
import { BudgetStoreService } from "../../services/store/budget/budget.store.service";
import { UserStoreService } from "../../services/store/user/user.store.service";
import { TablesFactory } from "../../components/tables/tables.factory";
import { TablesType } from "../../models/enum/tables.enum";
import { BudgetTable } from "../../components/tables/budget-tables/budget.table";
import { observeSmallScreen } from "../../shared/breakpoints";
import {
  BudgetUtils,
  BudgetIntervalSpend,
  BudgetPageInterval,
} from "../../models/view/budget/budget.utils";
import { BudgetView } from "../../models/view/budget/budget.view";
import { DropdownSelectMapOption } from "../../directives/dropdown.directive";
import { AutoBudgetService, SuggestedBudget } from "../plugins/auto-budget/auto-budget.service";
import { ConfirmationDialogService } from "../../services/confirmation/confirmation.service";
import { ConfirmationEnum } from "../../models/enum/confirmation.enum";
import { NotificationEnum } from "../../models/enum/notification.enum";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { PluginConfigService } from "../../services/store/data-plugin/plugin-config.service";
import { BudgetClockService } from "./budget-clock.service";

export type BudgetListItem = { view: BudgetView; barWidth: number } & BudgetIntervalSpend;

/** Page-level period filter: a real cadence, or "all" to show every budget regardless of cadence. */
export type BudgetPeriodFilter = BudgetPageInterval | "all";

export type BudgetSummary = {
  total: number;
  spent: number;
  remaining: number;
  usedPct: number;
  barWidth: number;
  status: "under" | "approaching" | "over";
  daysLeft: number;
  monthLabel: string;
  /** Weekly header title parts (Week NN / date range / year); only used when the period is weekly */
  weekLabel: { week: string; range: string; year: string };
  /** Monthly header title parts (month / year); only used by the compact (stacked) title */
  monthLabelParts: { month: string; year: string };
};

@Component({
  selector: "budget",
  templateUrl: "budget.component.html",
  host: { class: "tw-flex tw-w-full tw-h-full" },
})
export class BudgetComponent {
  private dialogFactory: DialogFactory;
  private budgetStoreService: BudgetStoreService = inject(BudgetStoreService);
  private userStore = inject(UserStoreService);
  /** Local-day signal the interval-derived computeds depend on (read for the dependency only, they
   *  still measure with a fresh Date.now()), so they refresh across an interval reset. */
  private clock = inject(BudgetClockService);
  private autoBudgetService = inject(AutoBudgetService);
  private confirmService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  private pluginConfigService = inject(PluginConfigService);
  /** Whether the Auto-Create Budget plugin is enabled — the button is hidden once disabled. */
  protected autoBudgetPluginEnabled = computed(() =>
    this.pluginConfigService.isAutoBudgetPluginEnabled(),
  );
  /** True when the viewport is ≤ 639 px or the device UA-detects as mobile — matches
   *  details-wrapper's own mobile-layout check so nested detail panels never disagree with
   *  this page about whether to show the mobile layout (see observeSmallScreen doc). */
  protected isSmallScreen = observeSmallScreen();

  table: BudgetTable = new TablesFactory().get(TablesType.Budget, this);
  searchText = signal("");
  selectedPeriod = signal<BudgetPeriodFilter>("all");
  coreCurrency = computed(() => this.userStore.preferences.preferenceView()?.coreCurrency || "");

  /**
   * How compact the desktop summary header is, set by FitLevelDirective (see the template) so the
   * header stays on one line for as long as its actual content fits, compacting one piece at a
   * time instead of wrapping: 0 full · 1 stacked month/year title · 2 mobile-size metric pills ·
   * 3 icon-only Add New · 4 mobile-style header (title + icon button, pills on their own row).
   */
  protected headerFitLevel = signal(0);
  protected readonly headerMaxFitLevel = 4;
  protected compactTitle = computed(() => !this.isSmallScreen() && this.headerFitLevel() >= 1);
  protected compactPills = computed(() => !this.isSmallScreen() && this.headerFitLevel() >= 2);
  protected iconOnlyCreateButton = computed(() => this.headerFitLevel() >= 3);
  /** Mobile-style stacked header: always on small screens, on desktop once nothing else fits. */
  protected headerStacked = computed(() => this.isSmallScreen() || this.headerFitLevel() >= 4);

  /** Budget counts per cadence, feeding the Period filter menu's per-row counts. Counted over the
   *  search-filtered budgets (same predicate as budgetListItems) so they match the list rows. */
  private budgetCountsByPeriod = computed<{ all: number; monthly: number; weekly: number }>(() =>
    BudgetUtils.countByPeriod(this.table.data() ?? [], this.searchText()),
  );

  /**
   * Period filter menu: All budgets + each real cadence, with a count per row. Custom isn't
   * supported by the data model yet (BudgetIntervalType is monthly/weekly only) — it's shown
   * disabled at a 0 count as a placeholder matching the design, not a real filter.
   */
  periodOptions = computed<DropdownSelectMapOption[]>(() => {
    const counts = this.budgetCountsByPeriod();
    return [
      {
        realValue: "all",
        optionDisplayValue: "All budgets",
        selectedDisplayValue: "Period · All",
        suffixText: `${counts.all}`,
        suffixClass: "tw-text-dt-text-tertiary",
      },
      {
        realValue: "weekly",
        optionDisplayValue: "Weekly",
        selectedDisplayValue: `Weekly · ${counts.weekly}`,
        suffixText: `${counts.weekly}`,
        suffixClass: "tw-text-dt-text-tertiary",
      },
      {
        realValue: "monthly",
        optionDisplayValue: "Monthly",
        selectedDisplayValue: `Monthly · ${counts.monthly}`,
        suffixText: `${counts.monthly}`,
        suffixClass: "tw-text-dt-text-tertiary",
      },
      {
        realValue: "custom",
        optionDisplayValue: "Custom",
        selectedDisplayValue: "Custom · 0",
        suffixText: "0",
        suffixClass: "tw-text-dt-text-tertiary",
      },
    ];
  });

  /** Cadences not yet supported by the data model — disabled placeholders in the Period menu. */
  readonly unsupportedPeriodOptions = ["custom"];

  constructor() {
    this.dialogFactory = new DialogFactory();
  }

  /**
   * Every budget's current-interval spend, computed ONCE. The per-cadence usage marks and the
   * header summary derive from this — instead of each re-running calculateCurrentIntervalSpend +
   * getBudgetAmountInCoreCurrency over the budgets on every recompute. (The list computes its own
   * native-currency spend separately in budgetListItems.)
   */
  private budgetSpendBase = computed<BudgetListItem[]>(() => {
    const budgets = this.table.data() ?? [];
    const transactionsByCat = this.budgetStoreService.budgetTransactions.sortedCollection();
    this.clock.day(); // dependency only: re-run when the local day changes (an interval reset)
    const nowMs = Date.now();
    this.coreCurrency();

    return budgets.map((budget) => {
      const budgetedAmount = this.budgetStoreService.getBudgetAmountInCoreCurrency(budget);
      const spend = BudgetUtils.calculateCurrentIntervalSpend(
        budget,
        transactionsByCat,
        nowMs,
        budgetedAmount,
      );
      return { view: budget, ...spend, barWidth: Math.min(spend.usedPct, 100) };
    });
  });

  /** Total/spent in core currency for the budgets on a given cadence (reuses budgetSpendBase,
   *  which is core-valued via getBudgetAmountInCoreCurrency; matches the header's coreCurrency() label). */
  private periodTotals(period: BudgetPeriodFilter): {
    total: number;
    spent: number;
    usedPct: number;
  } {
    let total = 0;
    let spent = 0;
    for (const item of this.budgetSpendBase()) {
      if (period !== "all" && item.view.interval !== period) {
        continue;
      }
      total += item.budgeted;
      spent += item.spent;
    }
    return { total, spent, usedPct: total ? (spent / total) * 100 : 0 };
  }

  /**
   * Per-budget list items with each budget's own-cadence spend, in the budget's OWN currency
   * (amount + card label). Shows every budget when the period filter is "all", otherwise only
   * budgets whose cadence matches the selected period, plus the search filter.
   */
  budgetListItems = computed<BudgetListItem[]>(() => {
    const search = this.searchText();
    const period = this.selectedPeriod();
    const budgets = this.table.data() ?? [];
    const transactionsByCat = this.budgetStoreService.budgetTransactions.sortedCollection();
    this.clock.day(); // dependency only: re-run when the local day changes (an interval reset)
    const nowMs = Date.now();

    return BudgetUtils.filterBySearch(budgets, search)
      .filter((budget) => period === "all" || budget.interval === period)
      .map((budget) => {
        // List cards show each budget in its OWN currency (native amount + per-transaction convert).
        const convert = this.budgetStoreService.getBudgetCurrencyConverter(budget);
        const spend = BudgetUtils.calculateCurrentIntervalSpend(
          budget,
          transactionsByCat,
          nowMs,
          budget.amount ?? 0,
          undefined,
          convert,
        );
        return { view: budget, ...spend, barWidth: Math.min(spend.usedPct, 100) };
      });
  });

  /**
   * Aggregate summary for the page header — scoped to the selected period. Only budgets whose own
   * cadence matches the switcher are summed, in core currency. The title labels and days-left are
   * derived here from one instant, so they refresh together with the totals and never disagree.
   */
  budgetSummary = computed<BudgetSummary>(() => {
    const period = this.selectedPeriod();
    this.coreCurrency();
    const nowMs = Date.now();
    const { total, spent, usedPct } = this.periodTotals(period);
    const remaining = total - spent;

    return {
      total,
      spent,
      remaining,
      usedPct,
      barWidth: Math.min(usedPct, 100),
      status: BudgetUtils.budgetStatus(usedPct),
      // "all" has no single reset date; days-left falls back to the calendar-month reset, same as monthLabel below.
      daysLeft: BudgetUtils.getDaysLeftInInterval(
        period === "weekly" ? "weekly" : "monthly",
        nowMs,
      ),
      monthLabel: BudgetUtils.getPageIntervalLabel(nowMs),
      weekLabel: BudgetUtils.getWeekLabelParts(nowMs),
      monthLabelParts: BudgetUtils.getPageIntervalLabelParts(nowMs),
    };
  });

  /** Fill colour for a progress bar / legend dot based on the spend status band */
  statusBarClass(status: "under" | "approaching" | "over"): string {
    return BudgetUtils.statusBarClass(status);
  }

  /** Background/text classes for a budget card's cadence chip. */
  intervalChipClass(interval: BudgetPageInterval): string {
    return interval === "weekly"
      ? "tw-bg-color-status-info-bg tw-text-color-status-info-text"
      : "tw-bg-color-accent-today-bg tw-text-color-text-action";
  }

  createNew() {
    this.dialogFactory.addBudget((budget: BudgetView) => this.showSavedBudget(budget));
  }

  async autoCreateBudget() {
    const suggestions = this.autoBudgetService.computeSuggestedBudgets();
    if (suggestions === null) {
      // budget store hasn't finished loading yet
      this.confirmService.notify(NotificationEnum.info, "loading", "budgetsStillLoadingMessage");
      return;
    }
    if (suggestions.length === 0) {
      this.confirmService.notify(
        NotificationEnum.info,
        "noBudgetsCreatedTitle",
        "noSpendingHistoryMessage",
      );
      return;
    }

    const confirmed = await this.confirmService.confirmFor(ConfirmationEnum.autoCreatingBudget);
    if (!confirmed) {
      return;
    }

    this.dialogFactory.openAutoBudgetPreview(suggestions, (finalized) =>
      this.finalizeAutoCreatedBudgets(finalized),
    );
  }

  private async finalizeAutoCreatedBudgets(finalized: SuggestedBudget[]) {
    const count = await this.autoBudgetService.createBudgets(finalized);
    // auto-created budgets are always monthly, so show them like showSavedBudget does
    this.selectedPeriod.set("monthly");
    this.confirmService.notify(
      NotificationEnum.success,
      this.i18nService.t("budgetsCreatedTitle"),
      this.i18nService.t("budgetsCreatedMessage", count.toString()),
      false,
    );
  }

  selectPeriod(period: string) {
    // The list is scoped to the selected period, so clear any open detail that may no longer be listed.
    this.selectedPeriod.set(period as BudgetPeriodFilter);
    this.table.budgetToDetail.set(null);
  }

  private showSavedBudget(budget: BudgetView) {
    // "All" already lists the saved budget; narrowing to its cadence would silently hide every
    // budget on the other cadence. A specific cadence still follows the saved budget so it shows.
    if (this.selectedPeriod() === "all") {
      return;
    }
    if (budget.interval === "weekly" || budget.interval === "monthly") {
      this.selectedPeriod.set(budget.interval);
    }
  }
}
